import React, { useState } from "react";
import styled from "styled-components";
import moment from "moment";

import { Tag } from "../../../../design-system";
import Modal, { ModalView } from "./Modal";
import { AnalysisData } from "./analysisData";
import { QueueModel } from "./queueModel";
import { channelLabel } from "./leadUtils";

// Where the per channel breakdown and the past days went when the pipeline tab
// was folded into one view. Both are reference, not work, so both open from the
// results card rather than sitting above the queue.
//
// The per day funnel snapshots (Outreach/{account}/history) are NOT here: they
// have no client callable, only an MCP tool. Adding one is a functions change,
// which this work deliberately did not take on.

const VIEWS: ModalView[] = [
  { id: "channels", label: "By channel" },
  { id: "days", label: "Past days" },
];

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-4);
`;

const Cell = styled.div`
  padding: var(--space-4);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  background: var(--color-surface-subtle);
`;

const Value = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  font-variant-numeric: tabular-nums;
  margin-top: var(--space-2);
`;

const Hint = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin-top: 4px;
`;

const Days = styled.div`
  border-top: 1px solid var(--color-border-subtle);
`;

const Day = styled.button<{ $active: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: var(--space-4) 0;
  border: 0;
  border-bottom: 1px solid var(--color-border-subtle);
  background: none;
  cursor: pointer;
  font-family: var(--font-sans);
  opacity: ${(p) => (p.$active ? 1 : 0.82)};
`;

const DayTop = styled.div`
  display: flex;
  align-items: baseline;
  gap: var(--space-3);
  flex-wrap: wrap;
`;

const DayDate = styled.span`
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  font-variant-numeric: tabular-nums;
`;

const DayHeadline = styled.div`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text);
  margin-top: var(--space-2);
  line-height: var(--line-height-snug);
`;

const DayMeta = styled.div`
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
  margin-top: var(--space-2);
`;

const Message = styled.p`
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const TONE: Record<string, "danger" | "warning" | "success"> = {
  crit: "danger",
  warn: "warning",
  good: "success",
};

export type HistoryPanelProps = {
  analysis: AnalysisData;
  queue: QueueModel;
  onClose: () => void;
};

const HistoryPanel: React.FC<HistoryPanelProps> = ({
  analysis,
  queue,
  onClose,
}) => {
  const [view, setView] = useState("channels");
  const { analyses, current, setSelectedDate } = analysis;
  const byChannel = queue.funnel.byChannel;

  return (
    <Modal
      title="History"
      subtitle="Where the leads came from, and what every past day concluded."
      wide
      views={VIEWS}
      activeView={view}
      onView={setView}
      onClose={onClose}
    >
      {view === "channels" &&
        (byChannel.length === 0 ? (
          <Message>No leads yet.</Message>
        ) : (
          <Grid>
            {byChannel.map((c) => (
              <Cell key={c.channel}>
                <Tag variant="accent" size="sm">
                  {channelLabel(c.channel)}
                </Tag>
                <Value>{c.total.toLocaleString()}</Value>
                <Hint>
                  {c.active} active · {c.replied} replied or better · {c.won} won
                </Hint>
              </Cell>
            ))}
          </Grid>
        ))}

      {view === "days" &&
        (!analyses || analyses.length === 0 ? (
          <Message>No analysis has been posted yet.</Message>
        ) : (
          <Days>
            {analyses.map((a) => {
              const open = (a.findings ?? []).filter(
                (f) => f.status === "open"
              ).length;
              return (
                <Day
                  key={a.date}
                  type="button"
                  $active={a.date === current?.date}
                  onClick={() => {
                    setSelectedDate(a.date);
                    onClose();
                  }}
                >
                  <DayTop>
                    <DayDate>{a.date} UTC</DayDate>
                    {a.verdict && (
                      <Tag variant={TONE[a.verdict.tone] ?? "neutral"} size="sm">
                        {a.verdict.tone}
                      </Tag>
                    )}
                    {a.date === current?.date && (
                      <Tag variant="neutral" size="sm">
                        on screen
                      </Tag>
                    )}
                  </DayTop>
                  <DayHeadline>
                    {a.verdict?.headline ?? "No verdict written."}
                  </DayHeadline>
                  <DayMeta>
                    {open} still open of {(a.findings ?? []).length}
                    {a.generatedAt
                      ? ` · written ${moment(a.generatedAt).fromNow()}`
                      : ""}
                  </DayMeta>
                </Day>
              );
            })}
          </Days>
        ))}
    </Modal>
  );
};

export default HistoryPanel;
